<script>
import ModelFilter from "@/router/views/purchase_payments/filter";
import ModelIndex from "../models/index-v3";
import ConfirmationModal from "@/components/modals/purchase-payment-delete-confirmation.vue";
import StatusWidget from "@/components/widgets/statuses/status";

export default {
  extends: ModelIndex,
  components: {
    ConfirmationModal,
    ModelFilter
  },
  page: {
    title: "Purchase Payments"
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("menuitems.purchase_payments.text"));
      }
    },
    resourceRoute: { default: "purchase_payments" },
    modelVariable: { default: "purchase_payment" },
    denyCreate: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      title: this.lodash.startCase(this.$t("purchase_payments.plural")),
      items: [
        {
          text: this.lodash.startCase(this.$t("purchase_payments.singular")),
          href: "/purchase_payments",
          active: true
        }
      ],
      sortBy:
        (this.$route.query.sort &&
          this.$route.query.sort
            .split("|")
            .shift()
            .split(",")
            .shift()) ||
        " ticket_id",
      sortDesc:
        (this.$route.query.sort &&
          this.$route.query.sort
            .split("|")
            .shift()
            .split(",")
            .pop() === "desc") ||
        true,
      filterColumns: {
        ticket_id__in: {
          type: Array,
          label: this.lodash.startCase(this.$t("purchase_payments.ticket_id")),
          operator: "in",
          input_component: "ticket-multiselect-ajax"
        },
        "paymentables.paymentable_id__in": {
          type: Number,
          label: this.lodash.startCase(this.$t("purchase_payments.purchase_order_id")),
          operator: "in",
          input_component: "purchase-order-multiselect-ajax"
        },
        paid_at__between: {
          type: Array,
          label: this.lodash.startCase('tanggal pembayaran'),
          operator: "between",
          input_component: "datepicker-range",
          opens: "left",
        },
        "to_bank_account.customer_id__in": {
          type: Array,
          label: this.lodash.startCase(this.$t("purchase_payments.to")),
          operator: "in",
          input_component: "supplier-multiselect-ajax",
          params: {
            type__in: "brs,lcs,farmer"
          }
        },
        nominal: {
          type: Array,
          label: this.lodash.startCase(this.$t("purchase_payments.nominal")),
          operator: "between",
          input_component: "number-range"
        },
        has_paymentables_status: {
          type: Array,
          label: this.lodash.startCase("Status Pembayaran"),
          input_component: "purchase-payment-status-select"
        }
      },
      model: this.modelVariable,
      deleting: false
    };
  },
  computed: {
    columns() {
      return {
        ["purchase_payment"]: [
          // { key: 'index', label: 'No', sortable: false, style: { width: '1%' } },
          {
            key: "ticket_id",
            label: this.lodash.startCase(this.$t("purchase_payments.ticket_id")),
            thStyle: { width: "100px" },
            sortable: true,
            component: {
              props: ["item"],
              computed: {
                customLabel() {
                  return `${this.item.ticket.id}: ${this.item.ticket?.customer_type ? this.$t(`tickets.customer_types.${this.item.ticket.customer_type}`) : ''}`;
                }
              },
              template: '<router-link :to="`/tickets/${item.ticket_id}`">{{ customLabel }}</router-link>'
            }
          },
          {
            key: "paymentables",
            label: this.lodash.startCase(this.$t("purchase_payments.purchase_order_id")),
            thStyle: { maxWidth: "250px" },
            thClass: "text-center",
            component: {
              props: ["item"],
              methods: {
                label(paymentable) {
                  if (this.lodash.isEmpty(paymentable)) return "-";
                  let {
                    paymentable: { id: purchase_order_id, customer: { name: customer_name } = {} } = {}
                  } = paymentable;
                  return `${purchase_order_id} (${customer_name})`;
                }
              },
              template:
                "<div>" +
                '<template v-for="(paymentable, index) in item.paymentables">' +
                '<router-link :to="`/purchase_orders/${(paymentable || {}).paymentable_id}`">{{ label(paymentable) }}</router-link>' +
                '<span v-if="index < (item.paymentables || []).length - 1">, </span>' +
                "</template>" +
                "</div>"
            }
          },
          {
            key: "to_bank_account",
            label: this.lodash.startCase(this.$t("purchase_payments.to")),
            thStyle: { maxWidth: "250px" },
            component: {
              props: ["item"],
              template:
                '<router-link :to="`/customers/${(item || {}).to_bank_account.customer_id}`">{{ `${item.to_bank_account.customer.name } - ${ item.to_bank_account.account_number} - ${item.to_bank_account.bank.name}` }}</router-link>'
            }
          },
          {
            key: "paid_at",
            label: this.lodash.startCase(this.$t("purchase_payments.paid_at")),
            style: { width: "15%" },
            sortable: true,
            formatter: value =>
              this.$moment
                .utc(value)
                .local()
                .format("DD MMM YYYY")
          },
          {
            key: "credit",
            label: this.lodash.startCase("nominal pembayaran"),
            thStyle: { width: "150px" },
            tdClass: "text-end",
            sortable: true,
            formatter: value => {
              return this.$numeral(value).format("$ 0,[00]");
            }
          },
          {
            key: "purchase_payment_status",
            label: this.lodash.startCase(this.$t("purchase_payments.status")),
            thStyle: { minWidth: "50px" },
            style: { width: "15%" },
            formatter: value => this.lodash.startCase(value),
            component: {
              props: ["item"],
              computed: {
                isAllPaidOff() {
                  return this.item.paymentables.every(({ paymentable: { remaining_debt } }) => remaining_debt === 0);
                }
              },
              methods: {
                label(values) {
                  if (this.isAllPaidOff) return `
                    <span class="badge badge-soft-green-700 bg-green-100">${this.lodash.upperFirst(this.$t("paid off"))}</span>
                  `;
                  if (values.length === 1) {
                    let {
                      paymentable: { remaining_debt }
                    } = values[0];

                    return remaining_debt > 0
                      ? 
                      `
                        <span class="badge badge-soft-warning">
                          ${this.lodash.upperFirst(
                            this.$t("underpaid {nominal}", {
                              nominal: this.$numeral(remaining_debt).format("$ 0,0.[00]")
                            })
                          )}
                        </span>
                      `
                      
                      : `<span class="badge badge-soft-warning">
                            ${this.lodash.upperFirst(this.$t("overpaid"))}
                        </span>`
                  }

                  return (
                    values
                      .sort((a, b) => a.paymentable_id - b.paymentable_id)
                      .map(({ paymentable_id, paymentable: { remaining_debt } }) => {
                        let status = this.lodash.upperFirst(this.$t("overpaid"));

                        if (remaining_debt > 0)
                          this.lodash.upperFirst(
                            this.$t("underpaid {nominal}", {
                              nominal: this.$numeral(remaining_debt).format("$ 0,0.[00]")
                            })
                          );
                        else if (remaining_debt < 0) this.lodash.upperFirst(this.$t("overpaid"));

                        return `${this.$t("purchase_orders.singular")} ${paymentable_id} ${status}`;
                      })
                      .join("<br>") ?? "-"
                  );
                }
              },
              template: `<div v-html="label([...this.item.paymentables])"></div>`
            }
          },
          {
            key: "creator.name",
            label: this.lodash.startCase(this.$t("CreatedBy")),
            thStyle: { minWidth: "50px" },
            style: { width: "20%" },
            formatter: value => this.lodash.startCase(value)
          },
          { key: "action", label: this.$t("Action"), class: "text-center", style: { width: "1%" } }
        ]
      };
    }
  },
  methods: {
    handleDelete(value) {
      this.deleteConfirmAlert(value.id).then(result => {
        if (result) {
          this.deleting = true;
          this.delete(value)
            .then(() => this.$refs.table.$refs.table.refresh())
            .then(() => this.$refs["delete-confirmation"].hide())
            .then(() => this.$refs["delete-confirmation"].deleteSuccessAlert(this.getModelName(value)))
            .catch(error => {
              this.failedAlert(error);
              return null;
            })
            .finally(() => {
              this.deleting = false;
              this.$refs["delete-confirmation"].hide();
            });
        }
      });
    },
    deleteConfirmAlert(value) {
      return this.$refs["delete-confirmation"]
        .show({
          payment_id: value
        })
        .then({ value });
    },
    getModelName(value) {
      return value["id"];
    },
    delete(value) {
      return this.$axios.delete(`/payments/${this.getModelKey(value)}`);
    },
    fetchModels(page = 1) {
      return this.$axios
        .get("/payments", {
          params: {
            page: page,
            per_page: this.perPage,
            search: this.search,
            with: [
              "creator",
              "files",
              "from_bank_account.bank",
              "from_bank_account.customer",
              "purchase_order.ticket",
              "ticket",
              "paymentables.paymentable.customer",
              "to_bank_account.bank",
              "to_bank_account.customer"
            ].join(","),
            sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
            ...this.$route.params,
            ...this.filterParams
          }
        })
        .then(response => {
          const {
            data: { data, meta }
          } = response;
          this.models = data.map(datum => {
            return {
              ...datum,
              ...this.modelPolicy(datum)
            };
          });
          this.page = Number(meta.current_page);
          this.perPage = Number(meta.per_page);
          this.from = Number(meta.from);
          this.total = Number(meta.total);

          this.stats = [
            {
              title: this.lodash.startCase(this.$t("purchase_payments.count")),
              value: this.$numeral(this.total).format("0,0.[00] a"),
              valueTitle: this.$numeral(this.total).format("0,0.[00]"),
              wrapperClass: "col-12 col-md-6 col-xl-4",
              component: StatusWidget
            }
          ];

          return response;
        })
        .catch(error => this.failedAlert(error));
    },
    modelPolicy() {
      return {
        _denyShow: false,
        _denyEdit: true,
        _denyDelete: true
      };
    }
  }
};
</script>

<template>
  <Layout>
    <!-- <PageHeader v-if="title" :title="title" :items="items" /> -->
    <div v-if="stats.length" class="row">
      <div v-for="stat of stats" :key="stat.icon" class="col-6 col-md-4 col-xl-3">
        <Stat v-bind="stat" :loading="busy" />
      </div>
    </div>
    <div v-if="!lodash.isEmpty(filterColumns)" class="row">
      <model-filter
        v-model="filter"
        :columns="filterColumns"
        :modelVariable="modelVariable"
        :filterHandler="filterHandler"
      />
    </div>
    <div class="row">
      <model-index
        ref="table"
        class="col-12"
        :loading="loading"
        v-bind="$props"
        :models="models"
        :columns="columns"
        :busy.sync="busy"
        :models-paginated="modelsPaginated"
        :page.sync="page"
        :per-page.sync="perPage"
        :from="from"
        :total="totalFiltered"
        :search.sync="search"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter-params.sync="filterParams"
        @create-click="handleCreate"
        @export-click="handleExport"
        @show-click="handleShow"
        @edit-click="handleEdit"
        @delete-click="handleDelete"
      >
      </model-index>
      <confirmation-modal ref="delete-confirmation" :hide-header="false" :deleting="deleting"> </confirmation-modal>
    </div>
  </Layout>
</template>
